import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, ShieldAlert, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function PlatformSettings() {
  const [admins, setAdmins] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", mobile: "" });
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/platform/admins").then((r) => setAdmins(r.data)).catch(() => setAdmins([]));
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try { await api.post("/platform/admins", form); toast.success("Platform admin created"); setOpen(false); setForm({ name: "", email: "", password: "", mobile: "" }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  return (
    <div data-testid="platform-settings-page">
      <PageHeader title="Platform Settings" subtitle="Manage platform administrators and platform-level configuration."
        actions={<Button onClick={() => setOpen(true)} className="bg-blue-700 hover:bg-blue-800" data-testid="create-admin-button"><Plus className="h-4 w-4 mr-1.5" /> New Platform Admin</Button>} />

      <div className="bg-white border border-slate-200 rounded-md">
        <div className="px-5 py-3.5 border-b border-slate-200 flex items-center gap-2"><ShieldAlert className="h-4 w-4 text-blue-600" /><h3 className="font-display font-semibold text-slate-800">Platform Administrators</h3></div>
        {admins === null ? <div className="grid place-items-center py-16"><Loader2 className="h-5 w-5 animate-spin text-blue-600" /></div> : (
          <Table>
            <TableHeader><TableRow className="bg-slate-50"><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead></TableRow></TableHeader>
            <TableBody>
              {admins.map((a) => (
                <TableRow key={a.email} data-testid={`admin-row-${a.email}`}><TableCell className="font-medium text-slate-800">{a.name}</TableCell><TableCell className="text-slate-600">{a.email}</TableCell><TableCell><span className="text-xs font-mono bg-blue-50 text-blue-700 border border-blue-200 rounded px-2 py-0.5">Platform Super Admin</span></TableCell></TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="admin-dialog">
          <DialogHeader><DialogTitle>New Platform Administrator</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5"><Label>Full name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="admin-name-input" /></div>
            <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="admin-email-input" /></div>
            <div className="space-y-1.5"><Label>Password</Label><Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="admin-password-input" /></div>
            <div className="space-y-1.5"><Label>Mobile</Label><Input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} disabled={saving || !form.name || !form.email || !form.password} className="bg-blue-700 hover:bg-blue-800" data-testid="admin-save-button">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
